/**
 * 숫자 포맷팅 관련 유틸 함수 모듈
 * @module core/number/format
 */

import { truthyNumber } from '@/common/utils/validation';
import BigNumber from 'bignumber.js';
import { isPlainObject } from 'lodash-es';

const truthy = (...args: unknown[]): boolean => args.every(truthyNumber);

/**
 * 1000 -> '1,000', 1000.1234 -> '1,000.1234'
 */
export const numberWithComma = (v) => {
  const reg = /\B(?=(\d{3})+(?!\d))/g;
  if (truthy(v) || truthy(Number(v))) {
    if (Number.isInteger(v)) {
      return v.toString().replace(reg, ',');
    }
    if (!v) {
      return '';
    }
    const part = Number(v).toString().split('.');
    return part[0].replace(reg, ',') + (part[1] ? `.${part[1]}` : '');
  }
  return v;
};

/**
 * {@link numberWithComma} 과 달리 `Number#toString` 에 의한 **지수표기**(예: `1e-21`)를 쓰지 않는다.
 * {@link BigNumber} 로 고정소수 문자열을 만든 뒤 정수부에 천단위 콤마만 적용한다.
 * chartPopup 등 원시 숫자를 그대로 보여줄 때 지수표기 회피용.
 */
export const numberWithCommaNoExponent = (v: unknown): string | unknown => {
  const reg = /\B(?=(\d{3})+(?!\d))/g;
  if (!(truthy(v) || truthy(Number(v)))) {
    return v;
  }
  const num = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(num)) {
    return String(v);
  }

  const plain = new BigNumber(num).toFixed();
  const negative = plain.startsWith('-');
  const unsigned = negative ? plain.slice(1) : plain;
  const [intRaw, fracRaw] = unsigned.split('.');
  const intWithComma = intRaw.replace(reg, ',');
  const intPart = negative ? `-${intWithComma}` : intWithComma;
  return fracRaw !== undefined ? `${intPart}.${fracRaw}` : intPart;
};

export const formatNumberToLocale = (v: number, locale?: string) => {
  if (truthyNumber(v)) {
    return v.toLocaleString(locale);
  }
  return v;
};

/**
 * if decimalPlaces is 3
 * null -> null
 * undefined -> null
 * 'string' -> null
 * 1 -> 1
 * 1000 -> '1,000'
 * 1.12 -> 1.12
 * 1000.1234 -> 1,000.123
 * 1000.1236 -> 1,000.124
 * @param num
 * @param decimalPlaces
 */
export const getRoundedNumberOrNull = (num: unknown, decimalPlaces: number): number | null => {
  if (truthyNumber(num)) {
    return parseFloat(num.toFixed(decimalPlaces));
  }
  return null;
};

/**
 * if decimalPlaces is 3
 * null -> ''
 * undefined -> ''
 * 'string' -> ''
 * 1 -> '1'
 * 1000 -> '1,000'
 * 1.1 -> '1.1'
 * 1000.1234 -> '1,000.123'
 * 1000.1236 -> '1,000.124'
 * @param num
 * @param decimalPlaces
 */
export const getRoundedNumberWithComma = (num: unknown, decimalPlaces: number): string => {
  if (!truthyNumber(num)) {
    return '';
  }
  if (Number.isInteger(num)) {
    return numberWithComma(num);
  }
  return numberWithComma(parseFloat(num.toFixed(decimalPlaces)));
};

/**
 * 소수점 digit 자리까지 버림 처리하여 숫자로 반환
 *
 * **특징:**
 * - 반환 타입: `number` (숫자만 반환, comma 없음)
 * - 버림 처리: `Math.floor`를 사용하여 소수점 이하를 버림
 * - null/undefined 처리: 입력값을 그대로 반환
 *
 * 사용법 format.test.ts 참고
 *
 * @param num - 버림할 숫자
 * @param digit - 소수점 자리수 (기본값: 3)
 * @returns 소수점 digit 자리까지 버림 처리된 숫자
 */
export const roundToDigitNumber = (num: number, digit = 3): number => {
  if (num || num === 0) {
    return Math.floor(num * 10 ** digit) / 10 ** digit;
  }
  return num;
};

/**
 * 소수점 digit 자리까지 버림 처리하여 숫자로 반환
 *
 * **특징:**
 * - 반환 타입: `number` (숫자만 반환, comma 없음)
 * - null/undefined 처리: 입력값을 그대로 반환
 *
 * 사용법 format.test.ts 참고
 *
 * @param num - 소수점 처리할 숫자
 * @param option - 소수점 자리수 (기본값: 3), 반올림 모드 (기본값: 'floor')
 * @returns 소수점 digit 자리까지 모드에 따라 처리된 숫자
 */
export const roundToDigitNumberByMode = (
  num: number,
  option: { digit: number; mode: 'floor' | 'ceil' | 'round' } = { digit: 3, mode: 'floor' },
): number => {
  const { digit, mode } = option;

  if (num || num === 0) {
    if (mode === 'ceil') return Math.ceil(num * 10 ** digit) / 10 ** digit;
    if (mode === 'round') return Math.round(num * 10 ** digit) / 10 ** digit;
    else return Math.floor(num * 10 ** digit) / 10 ** digit;
  }
  return num;
};

/**
 * 소수점 digit 자리까지 버림 처리하여 comma가 포함된 문자열로 반환
 *
 * **특징:**
 * - 반환 타입: `string | number` (comma 포함, 예: "1,234.56")
 * - 버림 처리: 소수점 이하를 버림
 * - bigint 지원: bigint나 큰 숫자는 comma만 추가하고 반올림하지 않음
 * - 지수 표기법 처리: 지수 표기법(e)이 포함된 경우 자동 처리, number 반환
 * - 소수점 자리수 처리: 소수점 자리수 처리 시, number 반환
 *
 * 사용법 format.test.ts 참고
 *
 * @param num - 버림할 숫자 또는 bigint
 * @param digit - 소수점 자리수 (기본값: 2)
 * @returns comma가 포함된 문자열 (예: "1,234.56")
 */
export const roundToDigit = (num: number | bigint, digit = 2): string | number => {
  if (num > Number.MAX_SAFE_INTEGER || typeof num === 'bigint') {
    return num.toLocaleString();
  }
  let convertNum = num;
  if (`${convertNum}`.includes('e')) {
    const [, exponent] = `${convertNum}`.split('e');
    convertNum = roundToDigitNumber(num, digit + Number(exponent));
  } else if (`${convertNum}`.includes('.')) {
    const arr = `${convertNum}`.split('.');
    convertNum = +`${arr[0]}.${arr[1].substring(0, digit + 2)}`;
  }
  if (convertNum || convertNum === 0) {
    return (+`${Math.floor(+`${convertNum}e+${digit}`)}e-${digit}`).toLocaleString(undefined, {
      maximumFractionDigits: digit,
    });
  }
  return convertNum;
};

/**
 * 소수점 digit 자리까지 버림 처리 (null/undefined 명시적 처리 및 string 입력 지원)
 *
 * **특징:**
 * - 반환 타입: `string | number | null`
 * - null/undefined 처리: 명시적으로 `null` 반환 (다른 함수들은 입력값 그대로 반환)
 * - string 입력 지원: comma가 포함된 문자열도 처리 가능
 * - 내부적으로 `roundToDigit`를 사용하여 comma 포함 문자열로 반환
 *
 * 사용법 format.test.ts 참고
 *
 * @param value - 버림할 값 (number, bigint, string, null, undefined)
 * @param digit - 소수점 자리수 (기본값: 3)
 * @returns comma가 포함된 문자열 또는 null
 */
export const roundToDigitValue = (
  value: number | bigint | string | null | undefined,
  digit = 3,
): string | number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return roundToDigit(value === '' ? 0 : parseFloat(value.replace(/,/g, '')), digit);
  }
  return roundToDigit(value, digit);
};

export const formatNumWithCommasAndDecimal = (num: number, digits = 0) => {
  if (num || num === 0) {
    return num.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  }
  return num;
};

export const formatNumWithCommasAndDecimalStr = (
  num: number,
  digits = 0,
  notNumText = '',
): string => {
  const result = formatNumWithCommasAndDecimal(num, digits);
  if (typeof result === 'string') {
    return result;
  }
  return notNumText;
};

/**
 * 소수점 digit 자리까지 0 방향으로 버림(Math.trunc) 후 comma 포함 문자열로 반환
 * - 비정상값(NaN/Infinity)은 '-' 반환
 * - trailing zero 제거
 */
export const formatTruncatedNumberWithComma = (value: number, digit = 3): string => {
  if (!Number.isFinite(value)) return '-';
  if (Number.isInteger(value)) return numberWithComma(value);

  const scale = 10 ** digit;
  const truncated = Math.trunc(value * scale) / scale;
  const normalized = truncated.toFixed(digit).replace(/\.?0+$/, '');
  return numberWithComma(normalized);
};

export const convertUnitValue = (val: number | null, unit?: string, digits: number = 2): string => {
  if (typeof val === 'number' && !Number.isNaN(val)) {
    const valWithDigit = roundToDigitNumber(val, digits);
    const valWithDigitComma = numberWithComma(valWithDigit);
    return unit ? valWithDigitComma + unit : valWithDigitComma;
  }
  return '';
};

export const reverseFormatBytes = (valueString: string): string => {
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  const [value, unit] = valueString.split(' ');
  const unitIndex = sizes.indexOf(unit);
  const numericValue = parseFloat(value);

  if (!valueString || Number.isNaN(numericValue) || unitIndex === -1) {
    return '';
  }

  const bytes = numericValue * 1024 ** unitIndex;
  return `${getRoundedNumberWithComma(bytes, 3)} Bytes`;
};

export const isBigNumber = (value: any): value is BigNumber => value instanceof BigNumber;

export const serializeValue = (value: any): string => {
  if (isBigNumber(value)) {
    return value.toString();
  }
  if (isPlainObject(value)) {
    return JSON.stringify(value);
  }
  return value;
};

/**
 * 부동 소수점 오차 방지
 * @param value
 * @param precision - 소수점 자리수 (기본값: 10)
 * @returns 부동 소수점 오차 방지된 숫자
 */
export const normalizeFloat = (value: number, precision = 10) => {
  return parseFloat(value.toFixed(precision));
};
